/**
 * Twin table view for every chart — the a11y fallback, which is why it is mandatory.
 *
 * Numeric (right-aligned) columns get tabular-nums so digits line up vertically —
 * the opposite call from StatTile's hero figure, which stays proportional.
 */

import type { ReactNode } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { VIZ } from './tokens';

export interface VizTableProps {
  columns: string[];
  /**
   * A cell is usually a string or a number. A node is allowed so the table can carry
   * the same affordance as the chart it mirrors (a logo, a link) — it is the a11y
   * twin, not a downgrade.
   */
  rows: ReactNode[][];
  align?: ('left' | 'right')[];
}

export default function VizTable({ columns, rows, align }: VizTableProps) {
  const alignOf = (i: number): 'left' | 'right' => align?.[i] ?? (i === 0 ? 'left' : 'right');

  return (
    <Box sx={{ maxHeight: 340, overflow: 'auto', mx: -1 }}>
      <Table size="small" sx={{ '& td, & th': { px: 1.5, py: 1.5, border: 0 } }}>
        <TableHead>
          <TableRow>
            {columns.map((c, i) => (
              <TableCell
                key={c + i}
                align={alignOf(i)}
                sx={{
                  pt: '0 !important',
                  pb: '10px !important',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: VIZ.muted,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  borderBottom: `1px solid ${VIZ.border} !important`,
                }}
              >
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, ri) => (
            <TableRow
              key={ri}
              sx={{
                '& td': {
                  borderBottom: ri === rows.length - 1 ? 0 : '1px solid #F1F1F4',
                },
                '&:hover td': { bgcolor: VIZ.rowHover },
              }}
            >
              {r.map((v, i) => (
                <TableCell
                  key={i}
                  align={alignOf(i)}
                  sx={{
                    fontSize: 14,
                    // The first column names the row, so it carries the weight;
                    // the rest are values and stay regular.
                    fontWeight: i === 0 ? 500 : 400,
                    color: VIZ.ink,
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: alignOf(i) === 'right' ? 'tabular-nums' : undefined,
                  }}
                >
                  {v}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
